import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { API, authHeaders, fieldStyle, labelStyle } from '../api/ticketsApi';

/**
 * Ticket number field that fetches only that ticket (no full-list load).
 * Prefill / typed value → GET /api/tickets/{ticketNo}.
 */
export default function TicketNoLookup({
  value = '',
  onChange,
  onTicket,
  filter,
  productId = '',
  label = 'Ticket number',
  placeholder = 'Type ticket no and press Enter…',
  disabled = false,
  autoLookup = true,
}) {
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const lastFetched = useRef('');
  const onTicketRef = useRef(onTicket);
  const filterRef = useRef(filter);
  onTicketRef.current = onTicket;
  filterRef.current = filter;

  const fetchTicket = async (raw, { force = false } = {}) => {
    const ticketNo = String(raw || '').trim();
    if (!ticketNo) {
      lastFetched.current = '';
      setLookupError(null);
      onTicketRef.current?.(null);
      return null;
    }
    if (!force && lastFetched.current === ticketNo) return null;

    setLoading(true);
    setLookupError(null);
    try {
      const res = await axios.get(`${API}/${encodeURIComponent(ticketNo)}`, authHeaders());
      const ticket = res.data;
      if (!ticket?.ticket_no) {
        lastFetched.current = '';
        setLookupError('Ticket not found');
        onTicketRef.current?.(null);
        return null;
      }
      if (productId && ticket.product_id && ticket.product_id !== productId) {
        lastFetched.current = ticket.ticket_no;
        setLookupError('This ticket belongs to a different product than the global filter');
        onTicketRef.current?.(null);
        return null;
      }
      if (productId && !ticket.product_id) {
        lastFetched.current = ticket.ticket_no;
        setLookupError('This ticket has no product — clear the product filter to use it');
        onTicketRef.current?.(null);
        return null;
      }
      if (typeof filterRef.current === 'function' && !filterRef.current(ticket)) {
        lastFetched.current = ticket.ticket_no;
        setLookupError('This ticket is not eligible for this action');
        onTicketRef.current?.(null);
        return null;
      }
      lastFetched.current = ticket.ticket_no;
      onTicketRef.current?.(ticket);
      if (ticket.ticket_no !== ticketNo) {
        onChange?.(ticket.ticket_no);
      }
      return ticket;
    } catch (err) {
      lastFetched.current = '';
      setLookupError(err.response?.data?.detail || 'Ticket not found');
      onTicketRef.current?.(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Prefill / value change: debounce single-ticket fetch.
  useEffect(() => {
    if (!autoLookup) return undefined;
    const ticketNo = String(value || '').trim();
    if (!ticketNo) {
      lastFetched.current = '';
      setLookupError(null);
      onTicketRef.current?.(null);
      return undefined;
    }
    if (lastFetched.current === ticketNo) return undefined;
    const timer = setTimeout(() => {
      fetchTicket(ticketNo);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, autoLookup, productId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fetchTicket(value, { force: true });
    }
  };

  return (
    <div>
      <label className="tk-label" style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
        <input
          className="tk-field"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (String(value || '').trim()) fetchTicket(value, { force: false });
          }}
          placeholder={placeholder}
          disabled={disabled || loading}
          style={{ ...fieldStyle, flex: 1, fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
          autoComplete="off"
          spellCheck={false}
          aria-label={label}
        />
        <button
          type="button"
          className="tk-btn tk-btn-secondary tk-btn-sm"
          onClick={() => fetchTicket(value, { force: true })}
          disabled={disabled || loading || !String(value || '').trim()}
          style={{ flexShrink: 0 }}
        >
          <MagnifyingGlass size={14} weight="bold" />
          {loading ? 'Looking up…' : 'Lookup'}
        </button>
      </div>
      {lookupError && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#B91C1C' }}>{lookupError}</p>
      )}
      {!lookupError && loading && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#74777F' }}>Fetching ticket…</p>
      )}
    </div>
  );
}
